import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiBook, FiArrowLeft, FiEye, FiCalendar, FiClipboard, FiCheckCircle, FiZap } from 'react-icons/fi';

const API_URL = `${process.env.REACT_APP_API_URL}/api/case-studies`;

function CaseStudies() {
  const { id } = useParams();
  const [caseStudies, setCaseStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchCaseStudies();
  }, [filter]);

  const fetchCaseStudies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?limit=20`);
      if (response.data.success) {
        setCaseStudies(response.data.data);
        if (id) {
          const study = response.data.data.find(s => s.id === parseInt(id));
          if (study) {
            setSelectedStudy(study);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching case studies:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategories = () => {
    const categories = new Set(caseStudies.map(s => s.category));
    return Array.from(categories);
  };

  const filteredStudies = filter === 'all' 
    ? caseStudies 
    : caseStudies.filter(s => s.category === filter);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-500">Loading case studies...</span>
      </div>
    );
  }

  if (selectedStudy) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => setSelectedStudy(null)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 rounded-xl transition-colors mb-6"
          >
            <FiArrowLeft className="w-4 h-4" /> Back to Case Studies
          </button>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-card overflow-hidden"
          >
            {selectedStudy.featured_image && (
              <div className="h-64 sm:h-80 overflow-hidden">
                <img src={selectedStudy.featured_image} alt={selectedStudy.title} className="w-full h-full object-cover" />
              </div>
            )}

            <div className="p-6 sm:p-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-full">{selectedStudy.category}</span>
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <FiCalendar className="w-3.5 h-3.5" />
                  {new Date(selectedStudy.created_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <FiEye className="w-3.5 h-3.5" /> {selectedStudy.views} views
                </span>
              </div>

              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">{selectedStudy.title}</h1>
              <p className="text-gray-600 leading-relaxed mb-8">{selectedStudy.description}</p>

              {selectedStudy.case_background && (
                <div className="mb-6 p-5 bg-gray-50 rounded-2xl">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 mb-3">
                    <FiClipboard className="w-5 h-5 text-primary" /> Case Background
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{selectedStudy.case_background}</p>
                </div>
              )}

              {selectedStudy.legal_issue && (
                <div className="mb-6 p-5 bg-amber-50/50 rounded-2xl border border-amber-100">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 mb-3">
                    <FiBook className="w-5 h-5 text-amber-600" /> Legal Issue
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{selectedStudy.legal_issue}</p>
                </div>
              )}

              {selectedStudy.solution_provided && (
                <div className="mb-6 p-5 bg-blue-50/50 rounded-2xl border border-blue-100">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 mb-3">
                    <FiZap className="w-5 h-5 text-blue-600" /> Solution Provided
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{selectedStudy.solution_provided}</p>
                </div>
              )}

              {selectedStudy.outcome && (
                <div className="mb-6 p-5 bg-green-50/50 rounded-2xl border border-green-100">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 mb-3">
                    <FiCheckCircle className="w-5 h-5 text-green-600" /> Outcome & Results
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{selectedStudy.outcome}</p>
                </div>
              )}

              {selectedStudy.tags && (
                <div className="flex flex-wrap gap-2 pt-6 border-t border-gray-100">
                  {selectedStudy.tags.split(',').map((tag, idx) => (
                    <span key={idx} className="px-3 py-1 bg-primary/5 text-primary text-sm font-medium rounded-lg">
                      #{tag.trim()}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </motion.article>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary via-primary-light to-[#2a4a6f] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl font-bold mb-3 flex items-center justify-center gap-3"
          >
            <FiBook className="w-8 h-8" /> Case Studies & Success Stories
          </motion.h1>
          <p className="text-white/70">Learn from our successful legal cases and outcomes</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button 
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              filter === 'all' ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
            }`}
          >
            All Cases ({caseStudies.length})
          </button>
          {getCategories().map(category => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                filter === category ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {filteredStudies.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-card p-16 text-center">
            <FiBook className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No case studies found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStudies.map((study, idx) => (
              <motion.div 
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => setSelectedStudy(study)}
                className="bg-white rounded-2xl shadow-card overflow-hidden hover:shadow-card-hover transition-all duration-300 cursor-pointer group"
              >
                {study.featured_image && (
                  <div className="h-44 overflow-hidden">
                    <img src={study.featured_image} alt={study.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-5">
                  <span className="inline-flex px-2.5 py-0.5 bg-primary/5 text-primary text-xs font-medium rounded-lg mb-2">{study.category}</span>
                  <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">{study.title}</h3>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                    {study.description.substring(0, 120)}...
                  </p>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">
                      {new Date(study.created_at).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <FiEye className="w-3.5 h-3.5" /> {study.views}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CaseStudies;
